import React from "react";
import { Modal } from "react-bootstrap";
import { useGetLeaveMusterRollQuery } from "../leaveApi";

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const STATUS_META = {
  GOOD: { label: "Good", cls: "badge-linesuccess" },
  AVERAGE: { label: "Average", cls: "badge-linewarning" },
  POOR: { label: "Poor", cls: "badge-linedanger" },
};

// "Click the score" drill-down (2026-09-07, direct instruction) - the
// legacy frm_master_roll.cs grid (many employees x one month, day columns)
// transposed onto one employee x the trailing 12 complete months instead,
// since that's what justifying a consistency score actually needs. Backed
// by the same GET /leave/muster-roll/{id} the score itself was computed
// from (LeaveService.ComputeConsistencyAsync), so the numbers here always
// match what the Suggested for Leave panel showed.
const MusterRollModal = ({ employeeRecordGuid, onClose }) => {
  const { data, isFetching, error } = useGetLeaveMusterRollQuery(employeeRecordGuid, { skip: !employeeRecordGuid });
  const statusMeta = data ? STATUS_META[data.consistencyStatus] || STATUS_META.POOR : null;

  return (
    <Modal show onHide={onClose} size="xl" scrollable dialogClassName="muster-roll-modal">
      <Modal.Header closeButton>
        <Modal.Title>
          Muster Roll {data ? `- ${data.empName || ""} ${data.empLastName || ""} (#${data.empCode})` : ""}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isFetching && <p className="text-muted small">Loading...</p>}
        {error && <div className="alert alert-danger py-2 px-3">Could not load this guard's attendance history.</div>}
        {data && (
          <>
            <div className="d-flex align-items-center gap-2 mb-3">
              <span className="text-muted small">Consistency score, trailing 12 complete months:</span>
              <strong>{data.consistencyScore}</strong>
              <span className={`badge ${statusMeta.cls}`}>{statusMeta.label}</span>
            </div>
            <div className="table-responsive">
              <table className="table table-sm muster-roll-table">
                <tbody>
                  {data.months.map((m) => {
                    const label = `${MONTH_NAMES[m.month - 1]}/${m.year}`;
                    return (
                      <tr key={`${m.year}-${m.month}`} className={m.isBad ? "muster-roll-row-bad" : ""}>
                        <td className="muster-roll-month-label">{label}</td>
                        <td className="muster-roll-days-cell">
                          {m.onApprovedLeave ? (
                            <span className="badge badge-lineinfo">On Approved Leave</span>
                          ) : m.noDataAvailable ? (
                            <span className="badge badge-linesecondary">No Data</span>
                          ) : (
                            <div className="d-flex gap-1 flex-wrap">
                              {m.days.map((d) => (
                                <span
                                  key={d.date}
                                  title={`${d.date}: ${d.worked ? "Worked" : "Not worked"}`}
                                  className={`muster-roll-day-dot ${d.worked ? "worked" : ""}`}
                                />
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="muster-roll-stat-cell text-nowrap">
                          {m.onApprovedLeave || m.noDataAvailable ? (
                            <span className="text-muted">-</span>
                          ) : (
                            <>
                              {m.daysWorked}/{m.expectedWorkingDays}
                              {m.isBad && <span className="badge badge-linedanger ms-2">Short</span>}
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default MusterRollModal;
